import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BOOKS } from "@/lib/books";
import BookReader from "@/components/BookReader";
import GhostLotus from "@/components/GhostLotus";
import DriftingField from "@/components/DriftingField";
import AudiobookSection from "@/components/AudiobookSection";
import { StarJaliRule, PaisleyRule, LotusPondRule, RosetteChain } from "@/components/Mughal";

const DIVIDERS = [PaisleyRule, LotusPondRule, RosetteChain, StarJaliRule];

// A slow river of interior art filling the page's quiet spaces
const ART_STREAM_A = [
  ...["01", "14", "27", "40"].map((n) => `/books/pages/bollydoll-${n}.webp`),
  ...["07", "19", "31", "43"].map((n) => `/books/pages/cosmic-${n}.webp`),
];
const ART_STREAM_B = [
  ...["13", "37", "61", "85"].map((n) => `/books/pages/awakened-${n}.webp`),
  ...["07", "20", "33", "46"].map((n) => `/books/pages/bollydoll-${n}.webp`),
];
const ART_STREAM_C = [
  ...["01", "13", "25", "37"].map((n) => `/books/pages/cosmic-${n}.webp`),
  ...["01", "49"].map((n) => `/books/pages/awakened-${n}.webp`),
];

const ArtStream = ({ images, reverse = false }) => (
  <div aria-hidden="true" className="relative z-10 w-full overflow-hidden py-6">
    <div
      className="band-marquee flex w-max items-center gap-4"
      style={reverse ? { animationDirection: "reverse" } : {}}
    >
      {[...images, ...images].map((src, i) => (
        <img
          key={i}
          src={src}
          alt=""
          loading="lazy"
          className="h-40 w-auto shrink-0 opacity-80 md:h-56"
          style={{ boxShadow: "0 10px 30px rgba(94,63,14,0.15)" }}
        />
      ))}
    </div>
  </div>
);

const DRIFT_PAGES = [
  ...["01", "14", "27", "40"].map((n) => `/books/pages/bollydoll-${n}.webp`),
  ...["01", "07", "13", "19", "25", "31", "37", "43"].map((n) => `/books/pages/cosmic-${n}.webp`),
  ...["01", "13", "25", "37", "49", "61", "73", "85"].map((n) => `/books/pages/awakened-${n}.webp`),
];

const NUMERALS = ["I", "II", "III", "IV", "V", "VI"];

const Ornament = () => (
  <div aria-hidden="true" className="flex items-center gap-4">
    <span className="h-px w-14 bg-[#8a6a1f]/50" />
    <GhostLotus className="h-6 w-6 text-[#8a6a1f]/70" />
    <span className="h-px w-14 bg-[#8a6a1f]/50" />
  </div>
);

const BooksPage = () => {
  const [openBook, setOpenBook] = useState(null);

  return (
    <main data-testid="books-page" className="relative min-h-screen overflow-hidden bg-[#f6f1e5]">
      <DriftingField images={DRIFT_PAGES} count={12} opacity={0.1} size={230} />
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-6 pt-10 md:px-10">
        <Link
          data-testid="books-back-link"
          to="/"
          className="text-xs uppercase text-[#7a7160] transition-colors duration-500 hover:text-[#8a6a1f]"
          style={{ letterSpacing: "0.3em" }}
        >
          &larr; Amrita Sen
        </Link>
        <a
          data-testid="books-amrita-link"
          href="https://www.amritasen.com/amrita-sen-books.html"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs uppercase text-[#7a7160] transition-colors duration-500 hover:text-[#8a6a1f]"
          style={{ letterSpacing: "0.3em" }}
        >
          amritasen.com
        </a>
      </header>

      <div className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-4 pt-14 md:px-10 md:pt-20">
        <GhostLotus
          className="absolute -top-10 right-0 h-[46vh] w-auto text-[#8a6a1f] opacity-[0.1] md:right-10"
        />
        <motion.h1
          data-testid="books-title"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="font-masthead gold-text relative uppercase leading-none"
          style={{ fontSize: "clamp(3rem, 9vw, 8rem)", letterSpacing: "0.09em", fontWeight: 500 }}
        >
          Books
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 1.2 }}
          className="font-serif-display relative mt-4 italic text-[#7a7160]"
          style={{ fontSize: "clamp(0.95rem, 1.4vw, 1.3rem)", letterSpacing: "0.28em" }}
        >
          stories in print
        </motion.p>
        <StarJaliRule className="mt-8 max-w-md text-[#8a6a1f]/50" />
      </div>

      <ArtStream images={ART_STREAM_A} />

      <section data-testid="books-shelf" className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-16 md:px-10">
        {BOOKS.map((b, i) => (
          <div key={b.title}>
            <motion.article
              data-testid={`book-${i}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative py-10 md:py-12"
          >
            {/* ambient aura of the book's own art, washed onto the paper */}
            <img
              src={b.cover}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 -z-0 m-auto h-[130%] w-auto opacity-[0.12] saturate-150"
              style={{ filter: "blur(70px)" }}
            />
            {/* giant outlined numeral */}
            <span
              aria-hidden="true"
              className={`font-masthead pointer-events-none absolute top-1/2 -translate-y-1/2 select-none leading-none ${
                i % 2 === 0 ? "right-0 md:right-6" : "left-0 md:left-6"
              }`}
              style={{
                fontSize: "clamp(9rem, 22vw, 19rem)",
                color: "transparent",
                WebkitTextStroke: "1.5px rgba(138,106,31,0.16)",
              }}
            >
              {NUMERALS[i]}
            </span>

            <div
              className={`relative flex flex-col items-center gap-10 md:gap-14 ${
                i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
              }`}
            >
              <div className="w-full shrink-0 md:w-5/12">
                <button
                  data-testid={`book-cover-${i}`}
                  onClick={() => b.pages && b.pages.length > 1 && setOpenBook(b)}
                  aria-label={b.pages && b.pages.length > 1 ? `Open ${b.title}` : b.title}
                  className={`block w-full bg-[#fffdf7] p-4 transition-transform duration-700 md:p-5 ${
                    i % 2 === 0 ? "md:-rotate-1" : "md:rotate-1"
                  } hover:rotate-0 ${
                    b.pages && b.pages.length > 1 ? "cursor-pointer" : "cursor-default"
                  }`}
                  style={{
                    boxShadow:
                      "0 30px 60px rgba(94,63,14,0.18), 0 0 0 1px rgba(138,106,31,0.25), inset 0 0 0 6px #fffdf7, inset 0 0 0 7px rgba(138,106,31,0.18)",
                  }}
                >
                  <img
                    src={b.cover}
                    alt={`${b.title} — cover`}
                    loading="lazy"
                    className="w-full object-contain transition-transform duration-700 hover:scale-[1.02]"
                    style={{ maxHeight: "52vh" }}
                  />
                </button>
              </div>
              <div className="flex w-full flex-col items-center gap-5 text-center md:w-7/12 md:items-start md:text-left">
                <span
                  className="text-[10px] uppercase text-[#8a6a1f]"
                  style={{ letterSpacing: "0.4em" }}
                >
                  {b.note}
                </span>
                <h2 className="font-serif-display text-3xl font-light leading-tight text-[#211d15] md:text-5xl">
                  {b.title}
                </h2>
                <Ornament />
                <p className="font-serif-display max-w-xl text-lg font-light leading-relaxed text-[#211d15]/70 md:text-xl">
                  {b.blurb}
                </p>
                <div className="mt-1 flex flex-wrap items-center justify-center gap-4 md:justify-start">
                  {b.pages && b.pages.length > 1 && (
                    <button
                      data-testid={`book-inside-look-${i}`}
                      onClick={() => setOpenBook(b)}
                      className="border border-[#8a6a1f]/40 px-6 py-3 text-xs uppercase text-[#8a6a1f] transition-all duration-500 hover:bg-[#8a6a1f] hover:text-[#f6f1e5]"
                      style={{ letterSpacing: "0.35em" }}
                    >
                      Inside look
                    </button>
                  )}
                  {b.links &&
                    b.links.map((l) => (
                      <a
                        key={l.label}
                        data-testid={`book-store-link-${i}`}
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border border-[#8a6a1f]/40 px-6 py-3 text-xs uppercase text-[#8a6a1f] transition-all duration-500 hover:bg-[#8a6a1f] hover:text-[#f6f1e5]"
                        style={{ letterSpacing: "0.3em" }}
                      >
                        {l.label}
                      </a>
                    ))}
                </div>
              </div>
            </div>

            </motion.article>
            {i === 1 && <ArtStream images={ART_STREAM_C} reverse />}
            {i < BOOKS.length - 1 && (
              <div aria-hidden="true" className="flex justify-center px-4 py-2">
                {(() => {
                  const Rule = DIVIDERS[i % DIVIDERS.length];
                  return <Rule className="w-full max-w-2xl text-[#8a6a1f]/45" />;
                })()}
              </div>
            )}
          </div>
        ))}
      </section>

      <ArtStream images={ART_STREAM_B} reverse />

      <div className="mx-auto max-w-6xl px-6 pb-8 md:px-10">
        <LotusPondRule className="max-w-6xl text-[#8a6a1f]/50" />
      </div>
      <AudiobookSection />

      <BookReader book={openBook} onClose={() => setOpenBook(null)} />
      <div className="noise-overlay" aria-hidden="true" />
    </main>
  );
};

export default BooksPage;
